import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useAppSelector } from "store/hooks"

import { convertDataToOptions } from "utils/convertData"
import { useUserProfile } from "hooks/useUserProfile"
import { AddressFields } from "modals/components/contractorModalContent/AddressFields"

import { useAccountSettingsTabGuard } from "./navigationGuard"
import { AccountPageShell, Field, Footer, getEnterpriseProfileInitialState, isEnterpriseProfileDirty } from "./shared"
import { useUpdateEnterpriseAccount } from "./useUpdateEnterpriseAccount"

import styles from "./index.module.scss"

export const AccountProfilePage = () => {
  const { enterpriseCountry } = useUserProfile()
  const currentZip = useAppSelector(state => state.tempData.currentZip)
  const { enterprise, isLoading, updateEnterprise } = useUpdateEnterpriseAccount()
  const initialValues = useMemo(
    () => getEnterpriseProfileInitialState(enterprise, enterpriseCountry),
    [enterprise, enterpriseCountry],
  )
  const [profileValues, setProfileValues] = useState(initialValues)
  const isDirty = Boolean(enterprise && isEnterpriseProfileDirty(enterprise, enterpriseCountry, profileValues))

  const handleProfileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setProfileValues(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  useEffect(() => {
    setProfileValues(initialValues)
  }, [initialValues])

  const handleSave = useCallback(async () => {
    if (!profileValues.companyName.trim()) {
      toast.error("Company name is required")
      return false
    }

    return updateEnterprise({ profileValues })
  }, [profileValues, updateEnterprise])

  useAccountSettingsTabGuard({
    isDirty,
    isSaving: isLoading,
    onSave: handleSave,
  })

  useEffect(() => {
    if (!currentZip?.city) return

    setProfileValues(prev => ({
      ...prev,
      country: convertDataToOptions({ data: [currentZip.country] }, "id", "name"),
      zip: convertDataToOptions({ data: [currentZip] }, "id", "code"),
      state: convertDataToOptions({ data: [currentZip.state] }, "id", "name"),
      city: convertDataToOptions({ data: [currentZip.city] }, "id", "name"),
      suburb: currentZip.suburbs?.length ? convertDataToOptions({ data: [currentZip.suburbs[0]] }, "id", "name") : [],
    }))
  }, [currentZip])

  return (
    <AccountPageShell footer={<Footer isLoading={isLoading} isSaveDisabled={!isDirty} onSave={handleSave} />}>
      <h1 className={styles.title}>Profile</h1>
      <div className={`${styles.formGrid} ${styles.fullWidthForm}`}>
        <Field
          className={styles.fieldNarrow}
          label="Company name *"
          name="companyName"
          value={profileValues.companyName}
          onChange={handleProfileChange}
        />
        <AddressFields
          inputValues={profileValues}
          error={{ field: "", message: "" }}
          onInputChange={handleProfileChange}
          setInputValues={setProfileValues}
          layout="stacked"
        />
      </div>
    </AccountPageShell>
  )
}
